/**
 * Locale switcher for the marketing site.
 *
 * - Keeps the user on the same logical route, swapping only the locale prefix.
 * - Persists choice in `nv_locale` cookie (1 year, SameSite=Lax) so the
 *   Cloudflare Pages middleware short-circuits on the next visit.
 * - Uses a native <select> for built-in keyboard nav, screen-reader behavior,
 *   and mobile-friendly UX; no custom dropdown.
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LOCALES,
  LOCALE_METADATA,
  isSupportedLocale,
  DEFAULT_LOCALE,
  type SupportedLocale,
} from '@noxverify/i18n';

interface LanguageSwitcherProps {
  /** Visual variant — `light` is for dark backgrounds (e.g. footer). */
  variant?: 'default' | 'light';
  className?: string;
}

/** Strip a leading locale segment from a pathname, if present. */
function stripLocalePrefix(pathname: string): string {
  const match = /^\/([a-z]{2}(?:-[A-Za-z]+)?)(?=\/|$)/.exec(pathname);
  if (!match) return pathname || '/';
  const candidate = match[1];
  if (!isSupportedLocale(candidate)) return pathname || '/';
  const rest = pathname.slice(match[0].length);
  return rest.length > 0 ? rest : '/';
}

export function LanguageSwitcher({ variant = 'default', className = '' }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const current = (
    isSupportedLocale(i18n.resolvedLanguage ?? i18n.language)
      ? (i18n.resolvedLanguage ?? i18n.language)
      : DEFAULT_LOCALE
  ) as SupportedLocale;

  const onChange = useCallback(
    (next: SupportedLocale) => {
      const bare = stripLocalePrefix(location.pathname);
      const newPath =
        next === DEFAULT_LOCALE ? bare : `/${next}${bare === '/' ? '' : bare}`;
      navigate(newPath + location.search + location.hash);
      void i18n.changeLanguage(next);
      // Persist preference so the Pages middleware respects it next visit.
      // `Secure` is safe because the marketing site is HTTPS-only in prod (HSTS
      // preloaded). On localhost (http) the flag is ignored by the browser.
      document.cookie = `nv_locale=${next}; path=/; max-age=31536000; samesite=lax; secure`;
    },
    [i18n, location.hash, location.pathname, location.search, navigate],
  );

  const selectClasses =
    variant === 'light'
      ? 'rounded-sm border border-hairline-dark bg-transparent text-ink-invert px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light'
      : 'rounded-sm border border-hairline-strong bg-white text-ink px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary';

  return (
    <label className={`inline-flex items-center gap-2 ${className}`}>
      <span className="sr-only">{t('common:language.switcher')}</span>
      <select
        value={current}
        onChange={(e) => onChange(e.target.value as SupportedLocale)}
        className={selectClasses}
        aria-label={t('common:language.switcher')}
      >
        {SUPPORTED_LOCALES.map((l) => (
          <option key={l} value={l} className="text-ink">
            {LOCALE_METADATA[l].nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
